import {
  Flex,
  Box,
  Table,
  Checkbox,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
} from "@chakra-ui/react";
import React, { useEffect } from "react";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  SortingState,
  useReactTable,
} from "@tanstack/react-table";

// Custom components
import Card from "components/card/Card";
import Menu from "components/menu/MainMenu";

type RowObj = {
  formName: string;
  canInsert: boolean;
  canEdit: boolean;
  canView: boolean;
  canDelete: boolean;
  canTicketAssign: boolean;
};

const columnHelper = createColumnHelper<RowObj>();

// const columns = columnsDataCheck;
export default function CheckTable(props: {
  tableData: any;
  isLoadingRole: boolean;
  updateChecked: any;
}) {
  const { tableData, updateChecked } = props;
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("gray.200", "whiteAlpha.100");

  const columns = [
    columnHelper.accessor("formName", {
      id: "formName",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          Form Name
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Text color={textColor} fontSize="sm" fontWeight="700">
            {info.getValue()}
          </Text>
        </Flex>
      ),
    }),
    columnHelper.accessor("canInsert", {
      id: "canInsert",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          Add Rights
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Checkbox
            defaultChecked={info.getValue()}
            isChecked={info.getValue()}
            onChange={(e) => updateChecked(e, info)}
            colorScheme="brandScheme"
            me="10px"
          />
        </Flex>
      ),
    }),
    columnHelper.accessor("canEdit", {
      id: "canEdit",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          Edit Rights
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Checkbox
            defaultChecked={info.getValue()}
            isChecked={info.getValue()}
            onChange={(e) => updateChecked(e, info)}
            colorScheme="brandScheme"
            me="10px"
          />
        </Flex>
      ),
    }),
    columnHelper.accessor("canView", {
      id: "canView",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          View Rights
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Checkbox
            defaultChecked={info.getValue()}
            isChecked={info.getValue()}
            onChange={(e) => updateChecked(e, info)}
            colorScheme="brandScheme"
            me="10px"
          />
        </Flex>
      ),
    }),
    columnHelper.accessor("canDelete", {
      id: "canDelete",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          Delete Rights
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Checkbox
            defaultChecked={info.getValue()}
            isChecked={info.getValue()}
            onChange={(e) => updateChecked(e, info)}
            colorScheme="brandScheme"
            me="10px"
          />
        </Flex>
      ),
    }),

    columnHelper.accessor("canTicketAssign", {
      id: "canTicketAssign",
      header: () => (
        <Text
          justifyContent="space-between"
          align="center"
          fontSize={{ sm: "10px", lg: "12px" }}
          color="gray.400"
        >
          Ticket Assign
        </Text>
      ),
      cell: (info: any) => (
        <Flex align="center">
          <Checkbox
            disabled={info.row.original.formName != "Ticketing System"}
            defaultChecked={info.getValue()}
            isChecked={info.getValue()}
            onChange={(e) => updateChecked(e, info)}
            colorScheme="brandScheme"
            me="10px"
          />
        </Flex>
      ),
    }),
  ];
  const [data, setData] = React.useState(() => []);
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    debugTable: true,
  });
  useEffect(() => {
    setData(tableData);
  }, [tableData]);
  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: "scroll", lg: "hidden" }}
    >
      <Box>
        <Table variant="simple" color="gray.500" mb="24px" mt="12px">
          <Thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  return (
                    <Th
                      key={header.id}
                      colSpan={header.colSpan}
                      pe="10px"
                      borderColor={borderColor}
                      cursor="pointer"
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <Flex
                        justifyContent="space-between"
                        align="center"
                        fontSize={{ sm: "10px", lg: "12px" }}
                        color="gray.400"
                      >
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                        {{
                          asc: "",
                          desc: "",
                        }[header.column.getIsSorted() as string] ?? null}
                      </Flex>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody>
            {table
              .getRowModel()
              .rows.slice(0, 100)
              .map((row) => {
                return (
                  <Tr key={row.id}>
                    {row.getVisibleCells().map((cell) => {
                      return (
                        <Td
                          key={cell.id}
                          fontSize={{ sm: "14px" }}
                          minW={{ sm: "150px", md: "200px", lg: "auto" }}
                          borderColor="transparent"
                        >
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })}
          </Tbody>
        </Table>
      </Box>
    </Card>
  );
}
